<template>
  <transition name="promo-slide">
    <section v-if="visible" class="wl-promo-popup" role="dialog" aria-labelledby="promo-title"
      aria-describedby="promo-description" @keydown.esc.stop.prevent="dismiss">
      <button class="wl-promo-close" type="button" aria-label="Close feedback" @click="dismiss">
        <i class="fa fa-times" aria-hidden="true"></i>
      </button>
      <div class="wl-promo-badge"><i class="fa fa-comment" aria-hidden="true"></i> YOUR FEEDBACK</div>
      <h3 id="promo-title">How is BookedSolid working for you?</h3>
      <p id="promo-description">Tell us what you love or what we could improve. It's optional!</p>

      <p v-if="submitted" class="wl-promo-success" role="status">Thanks! Your feedback has been sent.</p>
      <form v-else class="wl-promo-form" :aria-busy="saving" @submit.prevent="submitFeedback">
        <label for="promo-feedback">Your feedback</label>
        <textarea id="promo-feedback" v-model="feedback" :disabled="saving" required maxlength="2000"
          rows="4" placeholder="What could we do better?" aria-describedby="promo-feedback-hint"></textarea>
        <p v-if="error" class="wl-promo-error" role="alert">{{ error }}</p>
        <button class="wl-promo-btn" type="submit" :disabled="!feedback.trim() || saving">
          {{ saving ? 'Sending...' : 'Send feedback' }}
        </button>
      </form>
    </section>
  </transition>
</template>

<script>
import { sendFeedback } from '@/app/admin-routes';

export default {
  name: 'PopUpPromo',
  data() {
    return {
      visible: false,
      revealTimer: null,
      storageKey: '',
      feedback: '',
      saving: false,
      submitted: false,
      error: '',
    };
  },
  mounted() {
    try {
      const email = localStorage.getItem('email')?.trim().toLowerCase();
      if (!email) {
        console.warn('Unable to show feedback prompt: no signed-in user email.');
        return;
      }
      this.storageKey = `bookedsolid:feedback-dismissed:${email}`;
      if (!localStorage.getItem(this.storageKey)) {
        this.revealTimer = setTimeout(() => { this.visible = true; }, 1500);
      }
    } catch (error) {
      console.error('Unable to read feedback prompt preference:', error);
    }
  },
  beforeUnmount() {
    clearTimeout(this.revealTimer);
  },
  methods: {
    rememberDismissal() {
      try {
        localStorage.setItem(this.storageKey, 'true');
      } catch (error) {
        console.error('Unable to remember feedback prompt dismissal:', error);
      }
    },
    dismiss() {
      this.rememberDismissal();
      this.visible = false;
    },
    async submitFeedback() {
      if (this.saving || this.submitted) return;
      const feedback = this.feedback.trim();
      if (!feedback || feedback.length > 2000) {
        this.error = 'Please enter between 1 and 2,000 characters of feedback.';
        return;
      }

      this.saving = true;
      this.error = '';

      try {
        const response = await sendFeedback(feedback);
        if (!response?.success) {
          throw new Error(response?.message || 'Unable to send your feedback. Please try again.');
        }
        this.submitted = true;
        this.rememberDismissal();
      } catch (error) {
        this.error = error?.response?.data?.message || error.message || 'Unable to send your feedback. Please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.wl-promo-popup {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 5000;
  box-sizing: border-box;
  width: min(calc(100vw - 40px), 340px);
  max-height: calc(100vh - 40px);
  max-height: calc(100dvh - 40px);
  overflow-y: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  padding: 22px 22px 18px;
  text-align: left;
  border-top: 4px solid $colourP;
}

.wl-promo-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
}

.wl-promo-badge {
  display: inline-block;
  background: linear-gradient(135deg, $colourP, $colourS);
  color: white;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 2px 8px;
  border-radius: 20px;
  margin-bottom: 10px;
}

h3 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
  padding-right: 16px;
}

p {
  margin: 0 0 10px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.55;
}

.wl-promo-form {
  margin-top: 14px;

  label {
    display: block;
    margin-bottom: 5px;
    color: #374151;
    font-size: 12px;
    font-weight: 600;
  }

  textarea {
    box-sizing: border-box;
    width: 100%;
    min-height: 38px;
    max-height: 180px;
    resize: vertical;
    padding: 7px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    color: #111827;
    font-size: 12px;
    font-family: inherit;

    &:focus {
      border-color: $colourP;
      outline: 2px solid rgba($colourP, 0.18);
      outline-offset: 1px;
    }
  }
}

.wl-promo-hint {
  margin-top: 6px;
  font-size: 11px;
}

.wl-promo-success {
  color: #166534;
}

.wl-promo-error {
  margin: 8px 0 0;
  color: #b91c1c;
}

.wl-promo-btn {
  width: 100%;
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  background: linear-gradient(135deg, $colourP, $colourS);
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.promo-slide-enter-active {
  transition: opacity 0.25s ease;
}

.promo-slide-leave-active {
  transition: opacity 0.2s ease-in;
}

.promo-slide-enter-from,
.promo-slide-leave-to {
  opacity: 0;
}
</style>